/** Browser-only refs that must not be saved to AppSync. */
export function isTransientImageRef(
	ref: string | null | undefined
): boolean {
	if (!ref?.trim()) {
		return false;
	}
	const value = ref.trim().toLowerCase();
	return value.startsWith('blob:') || value.startsWith('data:');
}

/** Returns the ref when safe to persist via `updateCard.image`. */
export function persistableImageRef(
	ref: string | null | undefined
): string | undefined {
	if (!ref?.trim() || isTransientImageRef(ref)) {
		return undefined;
	}
	return ref.trim();
}

/** Returns a displayable URL, or empty string when the ref cannot be loaded. */
export function displayableImageRef(
	ref: string | null | undefined
): string {
	return persistableImageRef(ref) ?? '';
}

/** Strips browser-only image refs from image card config fields. */
export function sanitizeImageCardRefs<
	T extends {
		imageRef?: string;
		imageRefLight?: string;
		imageRefDark?: string;
		image?: string;
	},
>(config: T): T {
	const next = { ...config };
	if (isTransientImageRef(next.imageRef)) {
		delete next.imageRef;
	}
	if (isTransientImageRef(next.imageRefLight)) {
		delete next.imageRefLight;
	}
	if (isTransientImageRef(next.imageRefDark)) {
		delete next.imageRefDark;
	}
	if (isTransientImageRef(next.image)) {
		delete next.image;
	}
	return next;
}

const IMAGE_REF_FIELDS = [
	'imageRef',
	'imageRefLight',
	'imageRefDark',
	'image',
] as const;

/** Mutates `target`, removing transient image refs in place (unlike spread + assign). */
export function applySanitizedImageCardRefsInPlace<
	T extends {
		imageRef?: string;
		imageRefLight?: string;
		imageRefDark?: string;
		image?: string;
	},
>(target: T): T {
	const sanitized = sanitizeImageCardRefs({ ...target });
	for (const key of IMAGE_REF_FIELDS) {
		const value = sanitized[key];
		if (value === undefined) {
			delete target[key];
		} else {
			target[key] = value;
		}
	}
	return target;
}
